import type { SiteLang } from "@/hooks/useLanguage";

type FoundationI18n = {
  seo: { title: string; description: string };
  breadcrumb: { about: string; foundation: string };
  hero: {
    kicker: string;
    subtitle: string;
    description: string;
    ctaActivities: string;
    ctaNetwork: string;
  };
  about: {
    kicker: string;
    title: string;
  };
  mission: { label: string; text: string };
  vision: { label: string; text: string };
  goals: { title: string; desc: string }[];
  quote: string;
  quoteAuthor: string;
  network: {
    kicker: string;
    title: string;
    subtitle: string;
  };
  activities: {
    kicker: string;
    title: string;
    subtitle: string;
    flipBack: string;
    readMore: string;
    contactText: string;
  };
  partnersSection: {
    kicker: string;
    title: string;
    subtitle: string;
  };
  supportCta: {
    kicker: string;
    title: string;
    description: string;
    emailLabel: string;
  };
};

const en: FoundationI18n = {
  seo: {
    title: "QbD Group Foundation | Making a Difference",
    description: "The QbD Group Foundation supports science, helps individuals facing health threats, protects the environment, and monitors social commitment across the organization.",
  },
  breadcrumb: { about: "About us", foundation: "Foundation" },
  hero: {
    kicker: "Investing in people",
    subtitle: "Improving the quality of life is in our DNA",
    description: "<strong class=\"text-primary-foreground\">At QbD Group, we invest in people every day.</strong> We give them the chance to grow and develop. We help them build solid teams and relations. And, of course, we ensure that they can count on stable processes and medical products that are safe for them.",
    ctaActivities: "See our activities",
    ctaNetwork: "Meet the network",
  },
  about: { kicker: "About the Foundation", title: "Making a real difference" },
  mission: {
    label: "Mission",
    text: "Improving the quality of life is in our DNA. The <strong class=\"text-primary-foreground\">QbD Group gladly supports many good causes</strong>: projects that really make a difference, driven by enthusiasm and goodwill.",
  },
  vision: {
    label: "Vision",
    text: "We wanted to take it a step further. To truly embed our social commitment in a structured way, the QbD Group launched the <strong class=\"text-primary-foreground\">'QbD Group Foundation'</strong> on December 23, 2022.",
  },
  goals: [
    { title: "Supporting science", desc: "With a focus on the activities of the QbD Group." },
    { title: "Helping individuals & families", desc: "Paying for education, treatments, and therapy for those facing health threats." },
    { title: "Protecting the environment", desc: "Supporting innovative research, reducing our carbon footprint, and engaging in activities like plogging." },
    { title: "Social commitment", desc: "Monitoring the QbD Group's social commitment with emphasis on CSR and sustainability." },
  ],
  quote: "\"With the QbD Group Foundation, we truly want to have a local impact wherever the QbD Group operates and has affiliates. The closer we are to our charity projects, the more tangible and visible they become, for us and for others. There is much suffering in the world, and we won't be able to solve it all, but if we start with things we can directly affect and see around us, then we are already a step ahead.\"",
  quoteAuthor: "Elly De Bruyn, CHRO",
  network: {
    kicker: "Our people",
    title: "The foundation network",
    subtitle: "The QbD Group Foundation seeks to involve colleagues in identifying charities and fundraising ideas that align with its core values. Making social commitment an integral part of our corporate social responsibility.",
  },
  activities: {
    kicker: "Community fundraising",
    title: "Foundation activities",
    subtitle: "Here is a brief overview of our foundation activities, showcasing the impact we've made together. Click any card to read more.",
    flipBack: "Tap to flip back →",
    readMore: "Click to read more →",
    contactText: "For more information or to get involved, please contact us at",
  },
  partnersSection: {
    kicker: "Charities we champion",
    title: "Our partners",
    subtitle: "Through the QbD Group Foundation, we proudly support a variety of charitable organizations dedicated to creating a better future.",
  },
  supportCta: {
    kicker: "Get involved",
    title: "Support the QbD Group Foundation",
    description: "Want to contribute to our mission or propose a partnership? We'd love to hear from you.",
    emailLabel: "Contact us",
  },
};

const es: FoundationI18n = {
  seo: {
    title: "QbD Group Foundation | Marcando la diferencia",
    description: "La QbD Group Foundation apoya la ciencia, ayuda a personas que enfrentan amenazas para su salud, protege el medio ambiente y monitoriza el compromiso social de la organización.",
  },
  breadcrumb: { about: "Sobre nosotros", foundation: "Fundación" },
  hero: {
    kicker: "Inversión en personas",
    subtitle: "Mejorar la calidad de vida está en nuestro ADN",
    description: "<strong class=\"text-primary-foreground\">En QbD Group, invertimos en las personas cada día.</strong> Les damos la oportunidad de crecer y desarrollarse. Les ayudamos a construir equipos sólidos y relaciones. Y, por supuesto, nos aseguramos de que puedan contar con procesos estables y productos médicos seguros.",
    ctaActivities: "Ver nuestras actividades",
    ctaNetwork: "Conoce la red",
  },
  about: { kicker: "Sobre la Fundación", title: "Marcando una diferencia real" },
  mission: {
    label: "Misión",
    text: "Mejorar la calidad de vida está en nuestro ADN. <strong class=\"text-primary-foreground\">QbD Group apoya con gusto muchas buenas causas</strong>: proyectos que realmente marcan la diferencia, impulsados por el entusiasmo y la buena voluntad.",
  },
  vision: {
    label: "Visión",
    text: "Queríamos ir un paso más allá. Para integrar realmente nuestro compromiso social de manera estructurada, QbD Group lanzó la <strong class=\"text-primary-foreground\">'QbD Group Foundation'</strong> el 23 de diciembre de 2022.",
  },
  goals: [
    { title: "Apoyar la ciencia", desc: "Con un enfoque en las actividades de QbD Group." },
    { title: "Ayudar a personas y familias", desc: "Financiando educación, tratamientos y terapias para quienes enfrentan amenazas a su salud." },
    { title: "Proteger el medio ambiente", desc: "Apoyando investigación innovadora, reduciendo nuestra huella de carbono y participando en actividades como el plogging." },
    { title: "Compromiso social", desc: "Monitorizando el compromiso social de QbD Group con énfasis en RSC y sostenibilidad." },
  ],
  quote: "\"Con la QbD Group Foundation, realmente queremos tener un impacto local allí donde QbD Group opera y tiene filiales. Cuanto más cerca estamos de nuestros proyectos solidarios, más tangibles y visibles se vuelven, para nosotros y para los demás. Hay mucho sufrimiento en el mundo, y no podremos resolverlo todo, pero si empezamos por las cosas que podemos afectar directamente y ver a nuestro alrededor, ya estamos un paso por delante.\"",
  quoteAuthor: "Elly De Bruyn, CHRO",
  network: {
    kicker: "Nuestra gente",
    title: "La red de la fundación",
    subtitle: "La QbD Group Foundation busca involucrar a los compañeros en la identificación de causas benéficas e ideas de recaudación alineadas con nuestros valores fundamentales. Haciendo del compromiso social una parte integral de nuestra responsabilidad social corporativa.",
  },
  activities: {
    kicker: "Recaudación y acción social",
    title: "Actividades de la fundación",
    subtitle: "Aquí tienes un breve resumen de las actividades de nuestra fundación, mostrando el impacto que hemos logrado juntos. Haz clic en cualquier tarjeta para saber más.",
    flipBack: "Toca para volver →",
    readMore: "Haz clic para saber más →",
    contactText: "Para más información o para participar, contáctanos en",
  },
  partnersSection: {
    kicker: "Causas que impulsamos",
    title: "Nuestros socios",
    subtitle: "A través de la QbD Group Foundation, apoyamos con orgullo a diversas organizaciones benéficas dedicadas a crear un futuro mejor.",
  },
  supportCta: {
    kicker: "Participa",
    title: "Apoya la QbD Group Foundation",
    description: "¿Quieres contribuir a nuestra misión o proponer una colaboración? Nos encantaría saber de ti.",
    emailLabel: "Contáctanos",
  },
};

const zh: FoundationI18n = {
  seo: {
    title: "QbD Group Foundation | 创造改变",
    description: "QbD Group Foundation 支持科学发展,帮助面临健康威胁的个人,保护环境,并监督整个组织的社会承诺。",
  },
  breadcrumb: { about: "关于我们", foundation: "基金会" },
  hero: {
    kicker: "关爱人才共创未来",
    subtitle: "改善生活质量已融入我们的基因",
    description: "<strong class=\"text-primary-foreground\">在 QbD Group,我们每天都在投资于人。</strong>我们给予他们成长与发展的机会,帮助他们建立稳固的团队与人际关系。当然,我们也确保他们能够依赖稳定的流程与安全的医疗产品。",
    ctaActivities: "查看我们的活动",
    ctaNetwork: "认识网络成员",
  },
  about: { kicker: "关于基金会", title: "创造真正的改变" },
  mission: {
    label: "使命",
    text: "改善生活质量已融入我们的基因。<strong class=\"text-primary-foreground\">QbD Group 乐于支持众多公益事业</strong>:由热情与善意驱动、真正能创造改变的项目。",
  },
  vision: {
    label: "愿景",
    text: "我们希望更进一步。为了以结构化的方式真正落实社会承诺,QbD Group 于 2022 年 12 月 23 日正式成立了 <strong class=\"text-primary-foreground\">「QbD Group Foundation」</strong>。",
  },
  goals: [
    { title: "支持科学", desc: "聚焦于 QbD Group 的相关活动。" },
    { title: "帮助个人与家庭", desc: "为面临健康威胁的人士支付教育、治疗与疗法费用。" },
    { title: "保护环境", desc: "支持创新研究、降低碳足迹,并参与如 plogging 等环保活动。" },
    { title: "社会承诺", desc: "监督 QbD Group 的社会承诺,重点关注企业社会责任与可持续发展。" },
  ],
  quote: "「通过 QbD Group Foundation,我们真正希望在 QbD Group 运营和设有分支机构的每个地方产生本地影响。我们离公益项目越近,它们对我们和他人来说就越具体、越可见。世界上有许多苦难,我们无法解决所有问题,但如果我们从能直接影响并身边可见的事情做起,就已经领先一步。」",
  quoteAuthor: "Elly De Bruyn,首席人力资源官",
  network: {
    kicker: "我们的团队",
    title: "基金会网络",
    subtitle: "QbD Group Foundation 致力于让同事们参与识别符合核心价值观的慈善机构与筹款创意,使社会承诺成为我们企业社会责任不可或缺的一部分。",
  },
  activities: {
    kicker: "公益项目实际行动",
    title: "基金会活动",
    subtitle: "以下是我们基金会活动的简要概述,展示了我们共同创造的影响。点击任意卡片即可了解更多。",
    flipBack: "点击翻回 →",
    readMore: "点击阅读更多 →",
    contactText: "如需更多信息或参与其中,请通过以下方式联系我们:",
  },
  partnersSection: {
    kicker: "携手同行",
    title: "我们的合作伙伴",
    subtitle: "通过 QbD Group Foundation,我们自豪地支持各类致力于创造美好未来的慈善机构。",
  },
  supportCta: {
    kicker: "参与其中",
    title: "支持 QbD Group Foundation",
    description: "希望为我们的使命做出贡献,或提议建立合作?我们期待您的来信。",
    emailLabel: "联系我们",
  },
};

const translations: Record<SiteLang, FoundationI18n> = { en, es, "zh-cn": zh };
export const getFoundationT = (lang: SiteLang) => translations[lang] ?? en;
